import { useOutletContext } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { Chip, ListItem, ListItemText, TypographyProps } from "@mui/material";

import Page from "~/components/Page";
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectAdminComments, apiGetProjectAdminSalesAttributes, apiGetProjectAssociatedEmails, apiRemoveProjectAdminComment } from "~/providers/api";
import { IOutlet } from "~/interfaces";
import { IAdminProject, IAdminComment, IAdminSalesAttributes, IAdminAssociatedEmails } from "~/providers/admin-interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import PaperWithTitle from '~/components/PaperWithTitle';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import AdminProjectChangeInternalDescModal from "~/modals/AdminProjectChangeInternalDescModal";
import AdminCommentModal from "~/modals/AdminCommentModal";
import { classNames, formatDate } from "~/utils/utils";
import RemoveDialog from "~/modals/RemoveDialog";
import AdminEditSalesAttributesModal from "~/modals/AdminEditSalesAttributesModal";
import { userStore } from "~/stores/user";
import WysiwygDisplay from "~/components/WysiwygDisplay";
import CommentItem from "~/components/CommentItem";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	errorMsg: string;
	project: IAdminProject;
	salesAttributes: IAdminSalesAttributes;
	projectDesc: IAdminProject;
	addEditComment: boolean;
	comments: Array<IAdminComment>;
	commentData: IAdminComment;
	removeComment: IAdminComment;
	showSalesAttrsEdit: boolean;
	associatedEmails: Array<IAdminAssociatedEmails>;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	errorMsg: "",
	project: null,
	salesAttributes: null,
	projectDesc: null,
	addEditComment: false,
	commentData: null,
	comments: [],
	removeComment: null,
	showSalesAttrsEdit: false,
	associatedEmails: [],
};

export default function ProjectBusinessPage() {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState, setState } = myUseState<IState>(INIT_STATE);
	const { userData } = userStore(userState => ({
		userData: userState.user,
	}));
	const { params }: IOutlet = useOutletContext();
	const addCommentIcon = <>
		<AddIcon />
		{ trans("projectBusinessPage.addComment") }
	</>;

	async function reloadEmails() {
		const associatedEmailsData = await apiGetProjectAssociatedEmails(params.projectId, lang);

		if (associatedEmailsData.noErrors) {
			updateState({
				associatedEmails: associatedEmailsData.data,
			});
		}
	}

	function onProjectDescSave(projectData: IAdminProject) {
		setState(prev => ({
			...prev,
			isDirty: false,
			projectDesc: null,
			project: {
				...prev.project,
				internalDescription: projectData.internalDescription,
				lastUpdate: projectData.lastUpdate,
			},
			...prev.salesAttributes
				? {
					salesAttributes: {
						...prev.salesAttributes,
						internalDescription: projectData.internalDescription,
					},
				}
				: {},
		}));
		reloadEmails();
	}

	function hasEditBtn(commentItem: IAdminComment) {
		return commentItem.ownerUserId === userData.rusId;
	}

	function hasRemoveBtn(commentItem: IAdminComment) {
		return userData.internalRole === "internal_admin" || commentItem.ownerUserId === userData.rusId;
	}

	function getActionStyle(): TypographyProps {
		const isRed = state.salesAttributes.actionValidFrom === null || new Date(state.salesAttributes.actionValidFrom).getTime() < Date.now();

		return {
			color: isRed
				? "#c01"
				: "#000",
		};
	}

	function getActionTitle() {
		const datePart = state.salesAttributes.actionValidFrom
			? ` ${trans("projectBusinessPage.attributes.actionRequiredTextAfterDate", { date: formatDate(new Date(state.salesAttributes.actionValidFrom), isCzech) })}`
			: ``;

		return `${trans("projectBusinessPage.attributes.actionRequiredText")}${datePart}`;
	}

	async function onCommentRemove(resolve: () => void, reject: (error: string) => void) {
		const removeData = await apiRemoveProjectAdminComment(params.projectId, state.removeComment.id, lang);

		if (removeData.noErrors) {
			setState(prev => {
				const comments = prev.comments.slice();
				const ind = comments.findIndex(item => item.id === prev.removeComment.id);

				if (ind !== -1) {
					comments.splice(ind, 1);
				}

				return {
					...prev,
					comments,
				};
			});
			resolve();
		} else {
			reject(removeData.errors.join(", "));
		}
	}

	function onCommentSave(newComment: IAdminComment) {
		const comments = state.comments.slice();
		const filtered = comments.filter(item => item.id === newComment.id);

		if (filtered.length) {
			Object.assign(filtered[0], newComment);
		} else {
			comments.unshift(newComment);
		}

		updateState({
			addEditComment: false,
			isDirty: false,
			comments,
		});
		reloadEmails();
	}

	function onAttrsEditSave(salesAttributes: IAdminSalesAttributes) {
		updateState({
			isDirty: false,
			showSalesAttrsEdit: false,
			salesAttributes,
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, commentsData, salesAttrsData, associatedEmailsData] = await Promise.all([
			apiGetProject("admin", params.projectId, lang),
			apiGetProjectAdminComments(params.projectId, lang),
			apiGetProjectAdminSalesAttributes(params.projectId, lang),
			apiGetProjectAssociatedEmails(params.projectId, lang),
		]);

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...commentsData.noErrors
				? {
					// seradime obracene
					comments: commentsData.data,
				}
				: {},
			...salesAttrsData.noErrors
				? {
					salesAttributes: salesAttrsData.data,
				}
				: {},
			...associatedEmailsData.noErrors
				? {
					associatedEmails: associatedEmailsData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...commentsData.errors,
				...salesAttrsData.errors,
				...associatedEmailsData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	/* eslint-disable no-magic-numbers */
	return <Page checkLogged={true} title={trans("projectBusinessPage.title")} breadcrumbs={getProjectBreadcrumb(trans, true, state.project, trans("projectBusinessPage.title"))}
		checkInternalRole={"salesman"} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.settings">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		<div className="dashboardGrid">
			{ state.project && <PaperWithTitle title={ trans("projectBusinessPage.internalTitle") } onClick={() => updateState({ projectDesc: state.project })}>
				<WysiwygDisplay className="projectBusinessPage__internalDesc" html={state.project.internalDescription || trans("empty")} />
			</PaperWithTitle> }
			{ state.salesAttributes && <PaperWithTitle title={ trans("projectBusinessPage.attributes") } onClick={() => updateState({ showSalesAttrsEdit: true })}>
				<div className="projectBusinessPage__attributes">
					{ state.salesAttributes.approvedAdvancedPlan !== null && <ListItem>
						<ListItemText primary={state.salesAttributes.approvedAdvancedPlan ? trans("projectBusinessPage.attributes.approvedAdvancedPlan") : trans("projectBusinessPage.attributes.approvedAdvancedPlan.off")} />
					</ListItem> }
					{ state.salesAttributes.actionRequired && <>
						<ListItem>
							<ListItemText primary={getActionTitle()}
								secondary={trans("projectBusinessPage.attributes.actionRequired")} primaryTypographyProps={getActionStyle()} />
						</ListItem>
						<ListItem>
							<ListItemText primary={state.salesAttributes.actionDescription} secondary={trans("projectBusinessPage.attributes.actionDescription")} />
						</ListItem>
					</> }
					<ListItem>
						<ListItemText primary={state.salesAttributes.heliosNumber || trans("empty")} secondary={trans("projectBusinessPage.attributes.heliosNumber")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={state.salesAttributes.orderNumber || trans("empty")} secondary={trans("projectBusinessPage.attributes.orderNumber")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={state.salesAttributes.companyNumber || trans("empty")} secondary={trans("projectBusinessPage.attributes.companyNumber")} />
					</ListItem>
				</div>
			</PaperWithTitle> }
			<PaperWithTitle title={ trans("projectBusinessPage.comments") } onClick={() => updateState({ addEditComment: true, commentData: null })} icon={addCommentIcon}>
				<div className="projectBusinessPage__commentsArea">
					{ state.comments.length === 0 && <p className="projectBusinessPage__emptyComments">{ trans("projectBusinessPage.emptyComments") }</p> }
					{ state.comments.length > 0 && <div className="projectBusinessPage__commentsList">
						{ state.comments.map(commentItem => <CommentItem key={commentItem.id} data={commentItem} isCzech={isCzech} hasEdit={hasEditBtn(commentItem)} hasRemove={hasRemoveBtn(commentItem)}
							onEdit={() => updateState({ addEditComment: true, commentData: commentItem })} onRemove={() => updateState({ removeComment: commentItem })} />) }
					</div> }
				</div>
			</PaperWithTitle>
			<PaperWithTitle title={ trans("projectBusinessPage.associatedEmails") }>
				<div className={classNames(["projectBusinessPage__associatedEmails", state.associatedEmails.length ? "" : "empty"])}>
					{ state.associatedEmails.length === 0 && <span>{ trans("projectBusinessPage.associatedEmails.empty") }</span> }
					{ state.associatedEmails.map(item => <Chip label={item.emailAddress} variant="outlined" key={item.id} />) }
				</div>
			</PaperWithTitle>
		</div>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} sx={{ marginTop: "var(--margin-large)" }} /> }
		{ state.projectDesc && <AdminProjectChangeInternalDescModal params={params} projectData={state.project} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ projectDesc: null, isDirty: false })} onSave={onProjectDescSave} /> }
		{ state.addEditComment && <AdminCommentModal params={params} commentData={state.commentData} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ addEditComment: false, isDirty: false })} onSave={onCommentSave} /> }
		{ state.removeComment && <RemoveDialog title={trans("removeCommentDialog.title")} desc={trans("removeCommentDialog.desc")} onClose={() => updateState({ removeComment: null })} onRemove={onCommentRemove} /> }
		{ state.showSalesAttrsEdit && <AdminEditSalesAttributesModal params={params} salesAttrData={state.salesAttributes} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ showSalesAttrsEdit: false, isDirty: false })} onSave={onAttrsEditSave} /> }
	</Page>;
}
